import React, { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Loader2, AlertCircle, CheckCircle, FileText, User, Calendar, Download, Check, X, RotateCcw, Eye, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentAppUser } from "@/hooks/useCurrentAppUser";
import { uuid } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { DOCUMENT_CATEGORIES, getCategoryLabel, shouldAutoLinkToAllCases } from "@/lib/constants/documentCategories";

const DOCUMENT_ACTOR_PROVIDER = "app";

const statusColors = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300"
};

// Helper: Sanitize filename
const sanitizeFileName = (name) => {
  return name
    .normalize('NFKC')
    .replace(/\s+/g, '_')
    .replace(/[^\w.\-()]/g, '_');
};

// Helper: Normalize storage key and detect bucket
const normalizeKeyAndBucket = (k) => {
  if (!k) return { key: k, bucket: 'documents' };
  
  // Check if it's an eligibility-pdfs path
  if (k.startsWith('eligibility-pdfs/')) {
    return { key: k.replace('eligibility-pdfs/', ''), bucket: 'eligibility-pdfs' };
  }
  
  // Default documents bucket
  const key = k.startsWith('documents/') ? k.slice('documents/'.length) : k;
  return { key, bucket: 'documents' };
};

// Helper: Detect content type
const detectType = (row) => {
  const t = (row?.content_type || '').toLowerCase();
  if (t) return t;
  const name = (row?.display_name || row?.original_filename || '').toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|avif)$/.test(name)) return 'image/*';
  if (/\.pdf$/.test(name)) return 'application/pdf';
  if (/\.(mp4|webm|mov)$/.test(name)) return 'video/*';
  if (/\.(mp3|wav|m4a|ogg)$/.test(name)) return 'audio/*';
  if (/\.(txt|csv|json|md|log)$/.test(name)) return 'text/plain';
  return 'application/octet-stream';
};

export default function DocumentsTab({ contactId }) {
  const { data: currentUser } = useCurrentAppUser();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [appId, setAppId] = useState(null);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [category, setCategory] = useState("");
  
  // Case linking state
  const [contactCases, setContactCases] = useState([]);
  const [linkToCase, setLinkToCase] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Action states
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingDoc, setRejectingDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Preview state
  const [preview, setPreview] = useState(null); // { row, url, type }
  const [previewLoading, setPreviewLoading] = useState(false);

  // Link to case modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingDoc, setLinkingDoc] = useState(null);
  const [linkingCaseIds, setLinkingCaseIds] = useState([]);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    initializeTab();
  }, [contactId]);

  // Refresh cases when tab becomes visible (in case a case was created elsewhere)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && contactId) {
        loadContactCases();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [contactId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preview) {
        document.body.style.overflow = '';
      }
    };
  }, [preview]);

  const initializeTab = async () => {
    console.log("[Contact/Documents] === INITIALIZING TAB ===");
    
    if (!contactId) {
      console.error("[Contact/Documents] ❌ Contact ID not found");
      setIsLoading(false);
      return;
    }

    console.log("[Contact/Documents] Contact ID:", contactId);
    console.log("[Contact/Documents] Current user:", currentUser?.email);

    try {
      // Try multiple methods to extract app ID
      let extractedAppId = null;
      
      // Method 1: Try from pathname
      const pathMatch = window.location.pathname.match(/\/apps\/([^\/]+)/);
      if (pathMatch) {
        extractedAppId = pathMatch[1];
        console.log("[Contact/Documents] App ID from path:", extractedAppId);
      }
      
      // Method 2: Try from hostname subdomain
      if (!extractedAppId) {
        const hostname = window.location.hostname;
        const subdomainMatch = hostname.match(/^(?:preview--)?([^.]+)/);
        if (subdomainMatch && subdomainMatch[1] && subdomainMatch[1] !== 'localhost') {
          extractedAppId = subdomainMatch[1];
          console.log("[Contact/Documents] App ID from subdomain:", extractedAppId);
        }
      }
      
      // Method 3: Try from window global
      if (!extractedAppId && window.__APP_ID__) {
        extractedAppId = window.__APP_ID__;
        console.log("[Contact/Documents] App ID from window global (__APP_ID__):", extractedAppId);
      }
      
      setAppId(extractedAppId);
      console.log("[Contact/Documents] Final App ID:", extractedAppId);

      // Load documents using Supabase client directly
      await loadDocuments();
      
      // Load cases where this contact is linked
      await loadContactCases();

    } catch (error) {
      console.error("[Contact/Documents] ❌ Initialization error:", error);
    }

    setIsLoading(false);
  };

  const loadContactCases = async () => {
    try {
      const { data, error } = await supabase
        .from('case_contacts')
        .select(`
          case_id,
          cases:case_id (
            id,
            visa_type,
            items:item_id (
              catalog:catalog_id (
                name
              )
            )
          )
        `)
        .eq('contact_id', contactId);

      if (error) {
        console.error("[Contact/Documents] Failed to load contact cases:", error);
        return;
      }

      const cases = (data || [])
        .filter(cc => cc.cases)
        .map(cc => ({
          id: cc.cases.id,
          label: cc.cases.visa_type || cc.cases.items?.catalog?.name || 'Caso sem nome'
        }));

      setContactCases(cases);
    } catch (error) {
      console.error("[Contact/Documents] Error loading cases:", error);
    }
  };

  const loadDocuments = async () => {
    console.log("[Contact/Documents] Loading documents for contact:", contactId);

    try {
      // 1. Load direct contact documents
      const { data: directDocs, error: directError } = await supabase
        .from('v_documents_with_users')
        .select(`
          id,
          storage_path,
          display_name,
          original_filename,
          content_type,
          size_bytes,
          category,
          status,
          uploaded_by_display_name,
          uploaded_by_email,
          reviewed_by_display_name,
          reviewed_by_email,
          reject_reason,
          reviewed_at,
          created_at
        `)
        .eq('owner_type', 'contact')
        .or(`owner_id.eq.${contactId},owner_contact_id.eq.${contactId}`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (directError) {
        console.error("[Contact/Documents] ❌ Failed to load direct documents:", directError);
      }

      // 2. Load documents linked via case_document_refs
      const { data: caseLinkedDocs, error: caseLinkedError } = await supabase
        .from('case_document_refs')
        .select(`
          document_id,
          case_id,
          source,
          documents:document_id (
            id,
            storage_path,
            name,
            original_filename,
            content_type,
            size_bytes,
            category,
            status,
            reject_reason,
            reviewed_at,
            created_at
          )
        `)
        .eq('linked_contact_id', contactId);

      if (caseLinkedError) {
        console.error("[Contact/Documents] ❌ Failed to load case-linked documents:", caseLinkedError);
      }

      // Transform case-linked docs to match format
      const transformedCaseLinkedDocs = (caseLinkedDocs || [])
        .filter(ref => ref.documents)
        .map(ref => ({
          id: ref.documents.id,
          storage_path: ref.documents.storage_path,
          display_name: ref.documents.name,
          original_filename: ref.documents.original_filename,
          content_type: ref.documents.content_type,
          size_bytes: ref.documents.size_bytes,
          category: ref.documents.category,
          status: ref.documents.status,
          reject_reason: ref.documents.reject_reason,
          reviewed_at: ref.documents.reviewed_at,
          created_at: ref.documents.created_at,
          linked_via_case: true,
          case_id: ref.case_id
        }));

      // 3. Combine and deduplicate by id
      const allDocs = [...(directDocs || [])];
      const existingIds = new Set(allDocs.map(d => d.id));

      for (const doc of transformedCaseLinkedDocs) {
        if (!existingIds.has(doc.id)) {
          allDocs.push(doc);
          existingIds.add(doc.id);
        }
      }

      // Sort by created_at descending
      allDocs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      console.log("[Contact/Documents] ✅ Loaded", directDocs?.length || 0, "direct +", transformedCaseLinkedDocs.length, "case-linked =", allDocs.length, "total documents");
      setDocuments(allDocs);

    } catch (error) {
      console.error("[Contact/Documents] Error loading documents:", error);
    }
  };

  const openPreview = async (row) => {
    setPreviewLoading(true);
    
    const { key, bucket } = normalizeKeyAndBucket(row.storage_path || row.path);
    if (!key) {
      console.error('[Contact/Documents] missing storage_path');
      alert('Cannot preview: No storage path available');
      setPreviewLoading(false);
      return;
    }

    console.log('[Contact/Documents] Generating preview URL for:', key, 'from bucket:', bucket);
    
    try {
      // Generate signed URL from Supabase Storage (detect correct bucket)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(key, 3600); // 1 hour expiry

      if (signedUrlError) {
        console.error('[Contact/Documents] Failed to generate signed URL:', signedUrlError);
        alert('Failed to generate preview URL');
        setPreviewLoading(false);
        return;
      }

      const url = signedUrlData.signedUrl;
      const type = detectType(row);
      
      console.log('[Contact/Documents] Preview URL generated:', url.substring(0, 100) + '...');
      console.log('[Contact/Documents] Detected type:', type);
      
      setPreview({ row, url, type });
      if (window.innerWidth < 768) {
        document.body.style.overflow = 'hidden';
      }

      // Add ESC key listener
      const handleEscape = (e) => {
        if (e.key === 'Escape') closePreview();
      };
      document.addEventListener('keydown', handleEscape);
      setPreview(p => ({ ...p, _cleanup: () => document.removeEventListener('keydown', handleEscape) }));

    } catch (error) {
      console.error('[Contact/Documents] Preview error:', error);
      alert('Error generating preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreview(prev => {
      if (prev?._cleanup) prev._cleanup();
      return null;
    });
    document.body.style.overflow = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("[Contact/Documents] File selected:", file.name, file.size, "bytes");
      setUploadFile(file);
      setDocName(file.name);
    }
  };

  const handleDownload = async (doc) => {
    console.log("[Contact/Documents] Downloading document:", doc.id);
    
    if (!doc.storage_path) {
      console.error("[Contact/Documents] ❌ Document has no storage_path:", doc.id);
      alert("Cannot download: Document has no stored file.");
      return;
    }

    try {
      const { key, bucket } = normalizeKeyAndBucket(doc.storage_path);
      
      // Generate signed URL for download (detect correct bucket)
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(key, 60); // 1 minute is enough for download

      if (error) {
        console.error("[Contact/Documents] ❌ Failed to generate download URL:", error);
        alert("Failed to generate download link");
        return;
      }

      console.log('[Contact/Documents] Opening download URL');
      
      // Create temporary link to trigger download
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = doc.display_name || doc.original_filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("[Contact/Documents] ❌ Download error:", error);
      alert("Error downloading document");
    }
  };

  const handleReviewDocument = async (doc, action, reason = null) => {
    console.log("[Contact/Documents] Reviewing document:", doc.id, "action:", action);
    
    if (!currentUser) {
      console.error("[Contact/Documents] ❌ Current user not loaded");
      alert("User information not available.");
      return;
    }

    setActionInProgress({ docId: doc.id, action });

    try {
      const { data, error } = await supabase.rpc('review_document_actor', {
        p_document_id: doc.id,
        p_action: action,
        p_reason: reason || null,
        p_provider: DOCUMENT_ACTOR_PROVIDER,
        p_external_user_id: String(currentUser?.id || "unknown"),
        p_email: String(currentUser?.email || "unknown@local"),
        p_first_name: String(currentUser?.first_name || "Unknown"),
        p_last_name: String(currentUser?.last_name || "")
      });

      if (error) {
        console.error("[Contact/Documents] ❌ Review RPC failed:", error);
        alert(`Failed to ${action} document: ${error.message}`);
        return;
      }

      console.log("[Contact/Documents] ✅ Document reviewed:", data);

      const actionText = action === 'approved' ? 'approved' : action === 'rejected' ? 'rejected' : 'reset to pending';
      alert(`Document successfully ${actionText}.`);

      await loadDocuments();

    } catch (error) {
      console.error("[Contact/Documents] ❌ Review error:", error);
      alert("Error reviewing document: " + error.message);
    } finally {
      setActionInProgress(null);
      setShowRejectDialog(false);
      setRejectingDoc(null);
      setRejectReason("");
    }
  };

  const initiateReject = (doc) => {
    setRejectingDoc(doc);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  const confirmReject = () => {
    if (rejectingDoc) {
      handleReviewDocument(rejectingDoc, 'rejected', rejectReason || null);
    }
  };

  // Link existing document to cases
  const initiateLinkToCase = (doc) => {
    setLinkingDoc(doc);
    setLinkingCaseIds([]);
    setShowLinkModal(true);
  };

  const confirmLinkToCase = async () => {
    if (!linkingDoc || linkingCaseIds.length === 0) return;
    
    setIsLinking(true);
    try {
      for (const caseId of linkingCaseIds) {
        const { error } = await supabase
          .from('document_case_links')
          .upsert({
            document_id: linkingDoc.id,
            case_id: caseId,
            linked_via: 'manual_post_upload',
            linked_by: currentUser?.id
          }, { 
            onConflict: 'document_id,case_id',
            ignoreDuplicates: true 
          });
        
        if (error && !error.message.includes('duplicate')) {
          console.error('Error linking document to case:', error);
        }
      }
      
      toast.success(`Documento vinculado a ${linkingCaseIds.length} caso(s)`);
      setShowLinkModal(false);
      setLinkingDoc(null);
      setLinkingCaseIds([]);
    } catch (err) {
      console.error('Error linking document:', err);
      toast.error('Erro ao vincular documento');
    } finally {
      setIsLinking(false);
    }
  };

  const handleUpload = async () => {
    console.log("[Contact/Documents] === STARTING UPLOAD ===");
    
    if (!contactId) {
      console.error("[Contact/Documents] ❌ Contact ID not found");
      toast.error("ID do contato não encontrado");
      return;
    }

    if (!uploadFile) {
      console.error("[Contact/Documents] ❌ No file selected");
      toast.error("Nenhum arquivo selecionado");
      return;
    }

    if (!docName || !category) {
      console.error("[Contact/Documents] ❌ Missing required fields");
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (!currentUser) {
      console.error("[Contact/Documents] ❌ Current user not loaded");
      toast.error("Informações do usuário não disponíveis");
      return;
    }

    setIsUploading(true);

    try {
      const docId = uuid();
      const safeName = sanitizeFileName(uploadFile.name);
      const path = `contacts/${contactId}/${docId}/original/${safeName}`;
      
      console.log("[Contact/Documents] Uploading", { contactId, docId, safeName, path, fileSize: uploadFile.size });

      // STEP A: Upload to Storage using Supabase client
      console.log("[Contact/Documents] Step A: Uploading to Storage...");
      
      const { data: storageData, error: storageError } = await supabase.storage
        .from('documents')
        .upload(path, uploadFile, {
          contentType: uploadFile.type || 'application/octet-stream',
          upsert: false
        });

      if (storageError) {
        console.error("[Contact/Documents] ❌ Upload failed:", storageError);
        toast.error(`Falha no upload: ${storageError.message}`);
        setIsUploading(false);
        return;
      }

      console.log("[Contact/Documents] ✅ Upload successful:", storageData);

      // STEP B: Save metadata via RPC
      console.log("[Contact/Documents] Step B: Saving metadata via RPC...");
      
      const { data: rpcData, error: rpcError } = await supabase.rpc('create_document_record_actor', {
        p_provider: DOCUMENT_ACTOR_PROVIDER,
        p_external_user_id: String(currentUser?.id || "unknown"),
        p_email: String(currentUser?.email || "unknown@local"),
        p_first_name: String(currentUser?.first_name || "Unknown"),
        p_last_name: String(currentUser?.last_name || ""),
        p_doc: {
          storage_path: path,
          owner_id: contactId,
          owner_contact_id: contactId,
          owner_case_id: null,
          association: "contact",
          original_filename: safeName,
          name: docName.trim(),
          category: category,
          content_type: uploadFile.type || null,
          size_bytes: uploadFile.size,
          uploaded_via: "contact_tab"
        }
      });

      if (rpcError) {
        console.error("[Contact/Documents] ❌ RPC failed:", rpcError);
        toast.error(`Falha ao salvar metadados: ${rpcError.message}`);
        setIsUploading(false);
        return;
      }

      const newDocId = rpcData?.id;
      console.log("[Contact/Documents] ✅ Metadata saved:", rpcData);

      // STEP C: Link document to cases
      if (newDocId) {
        if (shouldAutoLinkToAllCases(category)) {
          // Auto-link identity documents to ALL cases of this contact
          console.log("[Contact/Documents] Step C: Auto-linking identity doc to all cases...");
          for (const caseItem of contactCases) {
            await supabase.from('document_case_links').insert({
              document_id: newDocId,
              case_id: caseItem.id,
              linked_via: 'identity_auto',
              linked_by: currentUser?.id
            }).onConflict('document_id, case_id').ignore();
          }
          if (contactCases.length > 0) {
            toast.success(`Documento vinculado automaticamente a ${contactCases.length} caso(s)`);
          }
        } else if (linkToCase && selectedCaseIds.length > 0) {
          // Manual link to selected cases
          console.log("[Contact/Documents] Step C: Linking to", selectedCaseIds.length, "case(s)...");
          for (const caseId of selectedCaseIds) {
            await supabase.from('document_case_links').insert({
              document_id: newDocId,
              case_id: caseId,
              linked_via: 'manual',
              linked_by: currentUser?.id
            }).onConflict('document_id, case_id').ignore();
          }
          toast.success(`Documento vinculado a ${selectedCaseIds.length} caso(s)`);
        }
      }

      console.log("[Contact/Documents] ✅ Upload complete!");
      toast.success("Documento enviado com sucesso!");

      // Clear inputs
      setUploadFile(null);
      setDocName("");
      setCategory("");
      setLinkToCase(false);
      setSelectedCaseIds([]);
      const fileInput = document.getElementById('upload-file-input');
      if (fileInput) fileInput.value = '';

      // Refresh documents table
      await loadDocuments();

    } catch (error) {
      console.error("[Contact/Documents] ❌ Unexpected error:", error);
      toast.error(`Erro inesperado: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!contactId) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            ID do contato não encontrado. Não é possível carregar ou enviar documentos.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Card */}
      <Card className="border-2 border-indigo-200 bg-indigo-50/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Upload className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-900 mb-1">Enviar Documento</h3>
              <p className="text-sm text-indigo-700 mb-4">
                Faça upload de documentos para este contato
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="upload-file-input" className="text-indigo-900">Selecionar Arquivo *</Label>
                  <Input
                    id="upload-file-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="mt-1 bg-white cursor-pointer"
                  />
                </div>

                <div>
                  <Label htmlFor="doc-name" className="text-indigo-900">Nome do Documento</Label>
                  <Input
                    id="doc-name"
                    value={docName}
                    onChange={(e) => setDocName(e.target.value)}
                    placeholder="ex: Cópia do Passaporte"
                    disabled={isUploading}
                    className="mt-1 bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="text-indigo-900">Categoria *</Label>
                  <Select 
                    value={category} 
                    onValueChange={(val) => {
                      setCategory(val);
                      // Reset case linking when identity category is selected
                      if (shouldAutoLinkToAllCases(val)) {
                        setLinkToCase(false);
                        setSelectedCaseIds([]);
                      }
                    }}
                    disabled={isUploading}
                  >
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Identity document auto-link notice */}
              {category && shouldAutoLinkToAllCases(category) && contactCases.length > 0 && (
                <Alert className="mt-4 bg-blue-50 border-blue-200">
                  <Link2 className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Este documento será vinculado automaticamente a <strong>{contactCases.length} caso(s)</strong> deste contato.
                  </AlertDescription>
                </Alert>
              )}

              {/* Manual case linking option */}
              {category && !shouldAutoLinkToAllCases(category) && contactCases.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="link-to-case" 
                      checked={linkToCase}
                      onCheckedChange={setLinkToCase}
                      disabled={isUploading}
                    />
                    <Label htmlFor="link-to-case" className="text-indigo-900 cursor-pointer">
                      Vincular a um caso específico
                    </Label>
                  </div>
                  
                  {linkToCase && (
                    <div className="space-y-2 p-3 bg-white rounded-lg border">
                      <Label className="text-sm text-slate-600">
                        Selecione os casos ({selectedCaseIds.length} selecionados)
                      </Label>
                      {contactCases.map(c => (
                        <div key={c.id} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`case-${c.id}`}
                            checked={selectedCaseIds.includes(c.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCaseIds(prev => [...prev, c.id]);
                              } else {
                                setSelectedCaseIds(prev => prev.filter(id => id !== c.id));
                              }
                            }}
                            disabled={isUploading}
                          />
                          <Label htmlFor={`case-${c.id}`} className="cursor-pointer text-slate-700">
                            {c.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No cases message with refresh button */}
              {category && !shouldAutoLinkToAllCases(category) && contactCases.length === 0 && (
                <Alert className="mt-4 bg-slate-50 border-slate-200">
                  <AlertDescription className="text-slate-700 flex items-center justify-between">
                    <span>Este contato não possui casos vinculados.</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => loadContactCases()}
                      className="text-slate-600 hover:text-slate-800"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Atualizar
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleUpload}
                disabled={!uploadFile || !category || isUploading}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar e Salvar
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-4">Documentos Recentes</h2>
        
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
            <p className="text-slate-500">Carregando documentos...</p>
          </div>
        ) : documents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-600 mb-2">Nenhum documento encontrado</p>
              <p className="text-sm text-slate-500">Faça o upload do seu primeiro documento acima</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Nome do Documento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Enviado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Revisado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {documents.map((doc) => {
                    const fileName = doc.display_name || doc.original_filename || doc.storage_path?.split('/').pop() || '(unnamed)';
                    const isActionRunning = actionInProgress?.docId === doc.id;
                    
                    return (
                      <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="text-sm font-medium text-slate-900">
                              {fileName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {doc.category ? (
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(doc.category)}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {doc.status === 'rejected' && doc.reject_reason ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <Badge className={`${statusColors[doc.status]} border-0 text-xs`}>
                                      {doc.status}
                                      <AlertCircle className="w-3 h-3 ml-1 inline" />
                                    </Badge>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm"><strong>Reason:</strong> {doc.reject_reason}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Badge className={`${statusColors[doc.status] || 'bg-slate-100 text-slate-700'} border-0 text-xs`}>
                              {doc.status || 'pending'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-700">
                              {doc.uploaded_by_display_name || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {doc.reviewed_by_display_name ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-slate-400" />
                                <span className="text-sm text-slate-700">
                                  {doc.reviewed_by_display_name}
                                </span>
                              </div>
                              {doc.reviewed_at && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  <span className="text-xs text-slate-500">
                                    {safeFormat(new Date(doc.reviewed_at), "MMM d, yyyy")}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {doc.created_at ? safeFormat(new Date(doc.created_at), "MMM d, yyyy h:mm a") : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openPreview(doc)}
                              disabled={isActionRunning || !doc.storage_path || previewLoading}
                              className="h-8 px-2 text-slate-600 hover:text-indigo-600"
                              title="Preview"
                            >
                              {previewLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                            
                            {/* Link to Case Button */}
                            {contactCases.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => initiateLinkToCase(doc)}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-indigo-600"
                                title="Vincular a caso"
                              >
                                <Link2 className="w-4 h-4" />
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownload(doc)}
                              disabled={isActionRunning}
                              className="h-8 px-2 text-slate-600 hover:text-blue-600"
                              title="Baixar"
                            >
                              {isActionRunning && actionInProgress.action === 'download' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                            
                            {doc.status !== 'approved' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReviewDocument(doc, 'approved')}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-green-600"
                                title="Approve"
                              >
                                {isActionRunning && actionInProgress.action === 'approved' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            {doc.status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => initiateReject(doc)}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-red-600"
                                title="Reject"
                              >
                                {isActionRunning && actionInProgress.action === 'rejected' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            {(doc.status === 'approved' || doc.status === 'rejected') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReviewDocument(doc, 'reset')}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-amber-600"
                                title="Reset to Pending"
                              >
                                {isActionRunning && actionInProgress.action === 'reset' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-reason" className="mb-2 block">
              Reason (optional)
            </Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="h-24"
              disabled={actionInProgress?.action === 'rejected'}
            />
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRejectDialog(false);
                setRejectingDoc(null);
                setRejectReason("");
              }}
              disabled={actionInProgress?.action === 'rejected'}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionInProgress?.action === 'rejected'}
            >
              {actionInProgress?.action === 'rejected' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject Document"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to Case Dialog */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              Vincular Documento a Casos
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Documento: <strong>{linkingDoc?.display_name || linkingDoc?.original_filename}</strong>
            </p>
            
            <Label className="text-sm text-slate-700 mb-3 block">
              Selecione os casos ({linkingCaseIds.length} selecionados)
            </Label>
            
            <div className="space-y-2 max-h-60 overflow-y-auto p-3 bg-slate-50 rounded-lg border">
              {contactCases.map(c => (
                <div key={c.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`link-case-${c.id}`}
                    checked={linkingCaseIds.includes(c.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setLinkingCaseIds(prev => [...prev, c.id]);
                      } else {
                        setLinkingCaseIds(prev => prev.filter(id => id !== c.id));
                      }
                    }}
                    disabled={isLinking}
                  />
                  <Label htmlFor={`link-case-${c.id}`} className="cursor-pointer text-slate-700">
                    {c.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowLinkModal(false);
                setLinkingDoc(null);
                setLinkingCaseIds([]);
              }}
              disabled={isLinking}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmLinkToCase}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={isLinking || linkingCaseIds.length === 0}
            >
              {isLinking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Vinculando...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Vincular a {linkingCaseIds.length} caso(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: 12,
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'min(1000px,90vw)',
            boxShadow: '0 10px 30px rgba(0,0,0,.2)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <header style={{
              padding: '16px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '8px'
                }} title={preview.row?.display_name || preview.row?.original_filename || 'Document'}>
                  {preview.row?.display_name || preview.row?.original_filename || 'Document'}
                </div>
                
                {/* Document metadata */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#64748b' }}>
                  {preview.row?.category && (
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{preview.row.category}</span>
                    </div>
                  )}
                  
                  {preview.row?.size_bytes && (
                    <span>
                      {(preview.row.size_bytes / 1024).toFixed(1)} KB
                    </span>
                  )}
                  
                  {preview.row?.uploaded_by_display_name && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>by {preview.row.uploaded_by_display_name}</span>
                    </div>
                  )}
                  
                  <Badge className={`${statusColors[preview.row?.status]} text-xs`}>
                    {preview.row?.status || 'pending'}
                  </Badge>
                </div>
                
                {/* Rejection reason if applicable */}
                {preview.row?.status === 'rejected' && preview.row?.reject_reason && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <strong>Rejected:</strong> {preview.row.reject_reason}
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(preview.url, '_blank')}
                  disabled={!preview?.url}
                >
                  Open in new tab
                </Button>
                <Button size="sm" onClick={closePreview} autoFocus>
                  Close (ESC)
                </Button>
              </div>
            </header>
            <section style={{
              padding: 12,
              overflow: 'auto',
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {(() => {
                const t = (preview.type || '').toLowerCase();
                const name = preview.row?.display_name || preview.row?.original_filename || '';
                
                if (t.startsWith('image')) {
                  return <img src={preview.url} alt={name} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />;
                }
                if (t === 'application/pdf') {
                  return <iframe src={`${preview.url}#toolbar=1&navpanes=0&view=FitH`} style={{ width: '85vw', height: '80vh', border: 0 }} title={name} />;
                }
                if (t.startsWith('video')) {
                  return <video src={preview.url} controls style={{ maxWidth: '85vw', maxHeight: '80vh' }} />;
                }
                if (t.startsWith('audio')) {
                  return (
                    <div className="flex flex-col items-center gap-4">
                      <FileText className="w-16 h-16 text-slate-300" />
                      <audio src={preview.url} controls style={{ width: '60vw' }} />
                    </div>
                  );
                }
                if (t.startsWith('text')) {
                  return <iframe src={preview.url} style={{ width: '85vw', height: '80vh', border: 0 }} title={name} />;
                }
                return (
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No inline preview for this type.</p>
                    <p className="text-sm text-slate-500 mt-2">Use "Open in new tab" to view.</p>
                  </div>
                );
              })()}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
